import { jsx as _jsx } from "react/jsx-runtime";
import { observer } from "mobx-react";
import { useEffect, useRef } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { useWindowSize } from "../useWindowSize";
import { VideoPlayerContainer, VideoProcessingPreviewContainer, VideoProcessingPreviewText, VideoProcessingPreviewWrapper, } from "./VideoPlayer.styles";
import { EventCategory } from "healthex-common/analytics.dtos";
export const VideoPlayer = observer((props) => {
    const { hlsUrl, mp4Url, blobUrl, posterUrl, width, height, analyticsService, studyId, options } = props;
    const videoContainerRef = useRef(null);
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const windowSize = useWindowSize();
    const autoCreate16By9Video = !width && !height;
    if (autoCreate16By9Video) {
        useEffect(() => {
            var _a, _b, _c;
            if (videoContainerRef.current) {
                const width = (_b = (_a = videoContainerRef.current.parentElement) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : windowSize.width;
                if (((_c = videoRef === null || videoRef === void 0 ? void 0 : videoRef.current) === null || _c === void 0 ? void 0 : _c.lastElementChild) && videoRef.current.lastElementChild instanceof HTMLElement) {
                    videoRef.current.lastElementChild.style.width = `${width}px`;
                    videoRef.current.lastElementChild.style.height = `${(width / 16) * 9}px`;
                }
            }
        }, [videoContainerRef, videoRef, windowSize]);
    }
    useEffect(() => {
        var _a, _b;
        if (!playerRef.current && videoRef.current) {
            const videoElement = document.createElement("video-js");
            videoElement.classList.add("vjs-big-play-centered");
            videoRef.current.appendChild(videoElement);
            const commonOptions = {
                controls: true,
                poster: posterUrl,
                width,
                height,
                ...options,
            };
            let player = null;
            if (hlsUrl) {
                player = videojs(videoElement, {
                    sources: [{ src: hlsUrl, type: "application/x-mpegURL" }],
                    ...commonOptions,
                });
            }
            else if (mp4Url) {
                player = videojs(videoElement, {
                    sources: [{ src: mp4Url, type: "video/mp4" }],
                    ...commonOptions,
                });
            }
            else if (blobUrl) {
                player = videojs(videoElement, {
                    sources: [{ src: blobUrl, type: "video/mp4" }],
                    ...commonOptions,
                });
            }
            if (player) {
                playerRef.current = player;
                let hasTrackedCompletion = false;
                const metadata = {
                    videoUrl: (_b = (_a = hlsUrl !== null && hlsUrl !== void 0 ? hlsUrl : mp4Url) !== null && _a !== void 0 ? _a : blobUrl) !== null && _b !== void 0 ? _b : undefined,
                };
                player.on("play", () => {
                    hasTrackedCompletion = false;
                    void (analyticsService === null || analyticsService === void 0 ? void 0 : analyticsService.trackEvent({
                        category: EventCategory.VIDEO,
                        action: "PLAY",
                        studyId,
                        metadata,
                    }));
                });
                player.on("pause", () => {
                    void (analyticsService === null || analyticsService === void 0 ? void 0 : analyticsService.trackEvent({
                        category: EventCategory.VIDEO,
                        action: "PAUSE",
                        studyId,
                        metadata,
                    }));
                });
                player.on("timeupdate", () => {
                    const duration = player === null || player === void 0 ? void 0 : player.duration();
                    const currentTime = player === null || player === void 0 ? void 0 : player.currentTime();
                    if (typeof duration !== "number" ||
                        Number.isNaN(duration) ||
                        typeof currentTime !== "number" ||
                        Number.isNaN(currentTime)) {
                        return;
                    }
                    const remainingTime = duration - currentTime;
                    if (remainingTime <= 5 && !hasTrackedCompletion) {
                        hasTrackedCompletion = true;
                        void (analyticsService === null || analyticsService === void 0 ? void 0 : analyticsService.trackEvent({
                            category: EventCategory.VIDEO,
                            action: "COMPLETE",
                            studyId,
                            metadata,
                        }));
                    }
                });
                player.on("ended", () => {
                    void (analyticsService === null || analyticsService === void 0 ? void 0 : analyticsService.trackEvent({
                        category: EventCategory.VIDEO,
                        action: "ENDED",
                        studyId,
                        metadata,
                    }));
                });
            }
        }
    }, [hlsUrl, mp4Url, blobUrl, posterUrl, options]);
    useEffect(() => {
        const player = playerRef.current;
        return () => {
            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, [playerRef]);
    return (_jsx(VideoPlayerContainer, { ref: videoContainerRef, "data-vjs-player": true, ...props, children: _jsx("div", { ref: videoRef }) }));
});
export const VideoProcessingPreview = () => {
    const containerRef = useRef(null);
    const windowSize = useWindowSize();
    useEffect(() => {
        var _a, _b;
        if ((containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) && containerRef.current instanceof HTMLElement) {
            const width = (_b = (_a = containerRef.current.parentElement) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : windowSize.width;
            containerRef.current.style.width = `${width}px`;
            containerRef.current.style.height = `${(width / 16) * 9}px`;
        }
    }, [containerRef, windowSize]);
    return (_jsx(VideoProcessingPreviewContainer, { children: _jsx(VideoProcessingPreviewWrapper, { ref: containerRef, children: _jsx(VideoProcessingPreviewText, { children: "The video will be available here for preview once the upload is processed." }) }) }));
};
